import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import jwt from 'jsonwebtoken';

import { SmlsAnimation, SmlsButton, SmlsHeaderPlaceholder } from '@smiles/smiles-ui-kit-react';
import { getMember, updateMember } from 'services/core/services';
import { Config } from '@smiles/core-engine';
import { AddressType, CellPhoneType } from '@smiles/core-account/dist/Types/CommonTypes';
import { loginLiferay } from 'utils/liferay';

interface MemberModel {
  address: AddressType;
  cellPhone: CellPhoneType;
  email: string;
  firstName: string;
}

const ConfirmRegister: React.FC = () => {
  const JWT_COOKIE_NAME = process.env.REACT_APP_TOKEN_NAME || 'session-token';

  const location = window.location.origin;
  const upd = new Date().getTime();
  const redirectHome = `${location}/home?upd=${upd}`;
  const redirectMyAccount = `${location}/restrito/minha-conta/alterar-dados?upd=${upd}`;

  const { t: register } = useTranslation('confirm_register');
  const { t: callback } = useTranslation('callback');
  const { t: error } = useTranslation('generic_error');

  const [cookies, setCookie, removeCookie] = useCookies([JWT_COOKIE_NAME]);

  const [member, setMember] = useState<MemberModel>();
  const [loadConfirmRegister, setLoadConfirmRegister] = useState<boolean>(false);
  const [animationState, setAnimationState] = useState({ isLoading: false, isSuccess: false });
  const [message] = useState({
    error: [error('header')],
    loading: [callback('one_moment')],
    success: [callback('ready')],
  });

  const token: string = cookies[JWT_COOKIE_NAME] || '';
  const decoded = jwt.decode(token);

  const setClassMainPortal = useCallback(async () => {
    const getMainPortal = document.querySelector('.main-portal');
    await loginLiferay(token, decoded);
    return getMainPortal?.classList?.add('main__portal--container');
  }, []);

  const onFinishAnimation = useCallback(() => {
    if (!animationState.isSuccess) {
      window.location.href = redirectHome;
    }
  }, [animationState]);

  const getMemberData = useCallback(async (): Promise<void> => {
    const { data, success } = await getMember();
    if (success) {
      setMember(data);
      setAnimationState({ isLoading: false, isSuccess: true });
    } else {
      setAnimationState({ isLoading: false, isSuccess: false });
      onFinishAnimation();
    }
  }, [member]);

  const clearToken = () => {
    removeCookie(JWT_COOKIE_NAME, { path: '/' });
  };

  const onReviewRegister = useCallback(async () => {
    clearToken();
    window.location.href = redirectMyAccount;
  }, [cookies, member]);

  const onConfirmRegister = useCallback(async () => {
    setLoadConfirmRegister(true);
    await updateMember({
      contactType: 'EMAIL',
      regularData: {
        address: {
          ...(member?.address as AddressType),
        },
        email: String(member?.email),
      },
    });
    clearToken();
    window.location.href = redirectHome;
  }, [member]);

  useEffect(() => {
    setClassMainPortal();
    localStorage.setItem(JWT_COOKIE_NAME, token);
    sessionStorage.setItem(JWT_COOKIE_NAME, token);
    Config.saveTokenAuthUser({ accessToken: token });
    setTimeout(async () => {
      setAnimationState({ isLoading: true, isSuccess: false });
      await getMemberData();
    }, 1050);
  }, []);

  useEffect(() => {
    if (animationState.isSuccess) {
      onFinishAnimation();
    }
  }, [animationState]);

  return (
    <>
      <SmlsAnimation currentState={animationState} onExit={onFinishAnimation} message={message} finishMode="wait" />
      <section className="smls-register" style={{ opacity: animationState.isSuccess ? 10 : 0 }}>
        <aside className="smls-register__sidebar smls-register__sidebar--left">
          <article className="smls-register__content">
            <SmlsHeaderPlaceholder redirectUrl={window.location.origin} />
            <h3 className="smls-register__title smls-header-placeholder">{register('made_of_travel')}</h3>
          </article>
        </aside>
        <aside className="smls-register__sidebar smls-register__sidebar--right">
          <article className="smls-register__content">
            <div className="smls-register__head">
              <h4 className="smls-register__user">
                {register('register_user', { user: `${String(member?.firstName)}` })}
              </h4>
              <p className="smls-register__text">
                <Trans t={register} i18nKey="register_description" />
              </p>
              <ul className="smls-register__box">
                <li className="smls-register__box--list">
                  <span className="smls-register__box--label">E-mail</span>
                  <p className="smls-register__box--txt">{String(member?.email)}</p>
                </li>
                <li className="smls-register__box--list">
                  <span className="smls-register__box--label">Telefone</span>
                  <p className="smls-register__box--txt">{String(member?.cellPhone?.formatted?.display)}</p>
                </li>
              </ul>
            </div>
            <div className="smls-register__foot">
              <SmlsButton
                data-testid="onReviewRegister"
                text={register('button_primary')}
                disabled={loadConfirmRegister}
                onClick={onReviewRegister}
              />
              <SmlsButton
                data-testid="onConfirmRegister"
                text={register('button_secondary')}
                color="secondary"
                isLoading={loadConfirmRegister}
                onClick={onConfirmRegister}
              />
            </div>
          </article>
        </aside>
      </section>
    </>
  );
};

export default ConfirmRegister;
